if (!customElements.get('quick-modal')) {
  class QuickModal extends HTMLElement {
    abortController = null
    cachedProductHtml = null
    cachedProductHref = null
    #escapeHandler = null

    constructor() {
      super()
    }

    connectedCallback() {
      this.closeButton = this.querySelector('.js-quick-modal-close')
      this.content = this.querySelector('.js-quick-modal-content')
      this.init()
    }

    init() {
      this.addEventListener('click', this.close.bind(this))
      this.content.addEventListener('click', async (e) => {
        e.stopPropagation()
        // Hide accelerated checkout buttons if insider club upsell is checked
        if (this.querySelector('#insider-club-upsell')?.checked) {
          this.querySelector('product-form shopify-accelerated-checkout')?.classList.add('hidden')
        } else {
          this.querySelector('product-form shopify-accelerated-checkout')?.classList.remove(
            'hidden',
          )
        }

        reviewStarsClickHandler(e)
      })

      this.closeButton.addEventListener('click', this.close.bind(this))
      this.#escapeHandler = (e) => {
        if (e.key === 'Escape') this.close()
      }
      document.addEventListener('keydown', this.#escapeHandler)
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.#escapeHandler)
    }

    async open(isRebuyRecommendation = false) {
      this.classList.add('active')
      this.classList.add('loading')
      document.body.style.setProperty('overflow', 'hidden', 'important')
      this.cachedProductHref = this.productHref
      this.dataset.isRebuyRecommendation = isRebuyRecommendation
      await this.fetchProduct()
      await this.updateQuickAddModal()

      const productInfo = this.querySelector('product-info')
      if (productInfo.dataset.productId && RecentlyViewed) {
        RecentlyViewed.addProduct(
          productInfo.dataset.productId,
          productInfo.dataset.productCategory5,
        )
      }
      dataLayerPageView()
    }

    close() {
      this.classList.remove('active')
      document.body.style.removeProperty('overflow')
    }

    async fetchProduct() {
      if (!this.productHref) return
      this.abortController?.abort()
      this.abortController = new AbortController()
      this.viewFullDetails.href = this.productHref

      const url = new URL(this.productHref, window.location.origin)
      url.searchParams.set('view', 'pdp-liquid-quick-add')
      const fullUrl = url.toString()

      try {
        const response = await fetch(fullUrl, {
          signal: this.abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch product page: HTTP error ${response.status}`)
        }

        const responseText = await response.text()
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        this.cachedProductHtml = html
      } catch (error) {
        if (error.name === 'AbortError') {
          this.close()
          return
        } else {
          this.close()
          throw error
        }
      } finally {
        this.abortController = null
      }
    }

    clearQuickAddModal() {
      this.gallery.innerHTML = ''
      this.header.innerHTML = ''
      this.variantSelector.innerHTML = ''
      this.discountBarWrapper.innerHTML = ''
      this.productForm.innerHTML = ''
      this.colorSelector.innerHTML = ''
      this.packSizeSelector.innerHTML = ''
      this.insiderWrapper.innerHTML = ''
      this.uiFooter.innerHTML = ''
      this.usoWrapper.innerHTML = ''
      this.jsonDataWrapper.innerHTML = ''
      this.productDescription.innerHTML = ''
      this.sizeChartModalWrapper.innerHTML = ''
      this.expectedShippingMessage.innerHTML = ''
    }

    async updateQuickAddModal() {
      this.clearQuickAddModal()

      const productGallery = this.cachedProductHtml.querySelector('product-gallery')
      if (productGallery) {
        productGallery.dataset.disableZoom = true
        this.gallery.appendChild(productGallery)
        // Force play videos on Safari
        this.gallery.querySelectorAll('video').forEach((video) => {
          video.play()
        })
      }

      const productHeader = this.cachedProductHtml.querySelector('.liquid-pdp__header')
      if (productHeader) {
        this.header.appendChild(productHeader)
      }

      const productVariantSelector = this.cachedProductHtml.querySelector('variant-selects')
      if (productVariantSelector) {
        this.variantSelector.appendChild(productVariantSelector)
      }

      const productForm = this.cachedProductHtml.querySelector(
        '.liquid-pdp__builder-button-wrapper',
      )
      if (productForm) {
        this.productForm.appendChild(productForm)
      }

      const productColorSelector = this.cachedProductHtml.querySelector('product-colors')
      const isWeeklyDeal = this.dataset.weeklyDeal === 'true'

      function updateProductColorSelectorForWeeklyDeals(colorSelector) {
        if (!colorSelector) return colorSelector

        if (!isWeeklyDeal) return colorSelector

        const wrapper = colorSelector.querySelector('.liquid-pdp__product-color-swatches-wrapper')
        if (!wrapper) return colorSelector

        wrapper.querySelectorAll('label').forEach((label) => {
          const input = label.querySelector('input[data-is-weekly-deal]')
          if (!input || input.dataset.isWeeklyDeal !== 'true') {
            label.remove()
          } else {
            label.classList.remove('liquid-pdp__product-color-swatch--hidden')
          }
        })

        const clearanceWrapper = colorSelector.querySelector('.js-clearance-swatches')

        let hasClearanceSwatches = false
        if (clearanceWrapper) {
          clearanceWrapper.querySelectorAll('label').forEach((label) => {
            const input = label.querySelector('input[data-is-weekly-deal]')
            if (!input || input.dataset.isWeeklyDeal !== 'true') {
              label.remove()
            } else {
              label.classList.remove('liquid-pdp__product-color-swatch--hidden')
              hasClearanceSwatches = true
            }
          })
        }

        const toggleButton = wrapper.querySelector('.liquid-pdp__singles-swatches__toggle')
        const clearanceSwatchesTitle = colorSelector.querySelector('.js-clearance-swatches-title')
        if (toggleButton) toggleButton.remove()
        if (clearanceSwatchesTitle) clearanceSwatchesTitle.remove()
        if (clearanceWrapper && !hasClearanceSwatches) clearanceWrapper.remove()

        return colorSelector
      }

      const updatedProductColorSelector =
        updateProductColorSelectorForWeeklyDeals(productColorSelector)

      if (updatedProductColorSelector) {
        this.colorSelector.appendChild(updatedProductColorSelector)
      }

      const productPackSizeSelector = this.cachedProductHtml.querySelector('product-pack-selector')
      this.packSizeSelector.style.display = 'block'
      if (productPackSizeSelector) {
        this.packSizeSelector.appendChild(productPackSizeSelector)
        if (productPackSizeSelector.style.display === 'none') {
          this.packSizeSelector.style.display = 'none'
        }
      } else {
        this.packSizeSelector.style.display = 'none'
      }

      const productInsiderWrapper = this.cachedProductHtml.querySelector('.js-insider-club-upsell')
      if (productInsiderWrapper) {
        this.insiderWrapper.appendChild(productInsiderWrapper)
      }

      if (this.afterpayWidget?.connectedCallback) {
        this.afterpayWidget.connectedCallback()
      }

      const productUiFooter = this.cachedProductHtml.querySelector('.js-liquid-pdp__ui-footer')
      if (productUiFooter) {
        this.uiFooter.appendChild(productUiFooter)
        const style = window.getComputedStyle(productUiFooter)
        if (productUiFooter.style.display === 'none' || style.display === 'none') {
          this.uiFooter.style.display = 'none'
        }
      }

      const usoWrapper = this.cachedProductHtml.querySelector('.js-uso-wrapper')
      if (usoWrapper) {
        this.usoWrapper.appendChild(usoWrapper)
      } else {
        this.usoWrapper.style.display = 'none'
      }

      const productJsonData = this.cachedProductHtml.querySelector('pdp-product-context')
      if (productJsonData) {
        this.jsonDataWrapper.appendChild(productJsonData)
      }

      // TODO: Remove this once we have a proper product description for quick add modal
      // const productDescription = this.cachedProductHtml.querySelector('.liquid-pdp__details')
      const productDescription = null
      this.productDescription.style.display = 'block'
      if (productDescription) {
        this.productDescription.appendChild(productDescription)
        if (productDescription.style.display === 'none') {
          this.productDescription.style.display = 'none'
        }
      } else {
        this.productDescription.style.display = 'none'
      }

      const sizeChartModal = this.cachedProductHtml.querySelector('size-chart-modal')
      if (sizeChartModal) {
        this.sizeChartModalWrapper.appendChild(sizeChartModal)
      }

      const expectedShippingMessage = this.cachedProductHtml.querySelector(
        '.cart-summary__expected-shipping',
      )
      if (expectedShippingMessage) {
        this.expectedShippingMessage.appendChild(expectedShippingMessage)
      } else {
        this.expectedShippingMessage.style.display = 'none'
      }

      // Copy dataset from source product info to quick add product info
      const sourceDataset = this.cachedProductHtml.querySelector('product-info').dataset
      Object.entries(sourceDataset).forEach(([key, value]) => {
        this.productInfo.dataset[key] = value
      })
      // Override product info dataset for quick add modal
      this.productInfo.dataset.cartSource = 'Quick View'
      this.productInfo.dataset.pdpType = 'popup'
      this.productInfo.dataset.pageType = this.dataset.pageType
      if (this.dataset.showOnlyClearance) {
        this.productInfo.dataset.showOnlyClearance = this.dataset.showOnlyClearance
      }
      this.productInfo.dataset.updateUrl = 'false'
      this.productInfo.dataset.isRebuyRecommendation = this.dataset.isRebuyRecommendation
      this.productInfo.dataset.ruleId = this.dataset.ruleId

      if (this.productInfo.dataset.isWeeklyDeal === 'true') {
        this.packSizeSelector.classList.add('visually-hidden')
      } else {
        this.packSizeSelector.classList.remove('visually-hidden')
      }

      // Discount bar
      const productDiscountBar = this.cachedProductHtml.querySelector('discount-bar')
      if (productDiscountBar) {
        this.discountBarWrapper.appendChild(productDiscountBar)
        if (!productDiscountBar.classList.contains('hidden')) {
          this.discountBarWrapper.classList.remove('visually-hidden')
        } else {
          this.discountBarWrapper.classList.add('visually-hidden')
        }
      }

      this.classList.remove('loading')
    }

    get productHref() {
      return this.dataset.productHref
    }

    get gallery() {
      return this.querySelector('.js-quick-modal-gallery')
    }

    get header() {
      return this.querySelector('.js-quick-modal-header')
    }

    get discountBarWrapper() {
      return this.querySelector('.js-quick-modal-discount-bar-wrapper')
    }

    get variantSelector() {
      return this.querySelector('.js-quick-modal-variant-selector')
    }

    get productForm() {
      return this.querySelector('.js-product-form')
    }

    get colorSelector() {
      return this.querySelector('.js-quick-modal-color-selector')
    }

    get packSizeSelector() {
      return this.querySelector('.js-quick-modal-pack-size-selector')
    }

    get viewFullDetails() {
      return this.querySelector('.js-quick-modal-view-full-details')
    }

    get productInfo() {
      return this.querySelector('product-info')
    }

    get insiderWrapper() {
      return this.querySelector('.js-quick-modal-insider-wrapper')
    }

    get uiFooter() {
      return this.querySelector('.js-quick-modal-ui-footer')
    }

    get afterpayWidget() {
      return this.querySelector('afterpay-widget')
    }

    get usoWrapper() {
      return this.querySelector('.js-quick-modal-uso-wrapper')
    }

    get jsonDataWrapper() {
      return this.querySelector('.js-quick-modal-json-data')
    }

    get productDescription() {
      return this.querySelector('.js-quick-modal-product-description')
    }

    get sizeChartModalWrapper() {
      return this.querySelector('.size-chart-modal-wrapper')
    }

    get expectedShippingMessage() {
      return this.querySelector('.js-quick-modal-expected-shipping-message')
    }
  }
  customElements.define('quick-modal', QuickModal)
}
